// @ts-check
// Datas da agenda no fuso do navegador (a grade de horas é montada nele; a preferência de fuso vale nas listas e
// registros), com o primeiro dia da semana e o formato de hora do membro: intervalos de mês, semana e dia, grade do mês e posição dos eventos na
// grade de horas (colunas lado a lado quando os horários se sobrepõem).
import { LOCALE } from '../i18n/index.js';
import { formatTime as formatPreferredTime } from './format.js';
import { formatPreferencesState } from './format-preferences.js';

export const CALENDAR_VIEWS = /** @type {const} */ (['month', 'week', 'day']);
const DAY_MILLISECONDS = 24 * 60 * 60 * 1000;
const MINIMUM_EVENT_MINUTES = 30;

/** @param {Date} date */
export function startOfDay(date) {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/** @param {Date} date @param {number} days */
export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Início da semana: domingo (padrão dos calendários brasileiros) ou segunda, conforme a preferência do membro.
 * @param {Date} date @param {number} [startDay]
 */
export function startOfWeek(date, startDay = formatPreferencesState.value.calendarStartDay) {
  const day = startOfDay(date);
  return addDays(day, -((day.getDay() - startDay + 7) % 7));
}

/** @param {Date} date */
export function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/** @param {Date} left @param {Date} right */
export function isSameDay(left, right) {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
}

/** Data local em AAAA-MM-DD (chave do dia e valor de <input type="date">). @param {Date} date */
export function toDateKey(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** @param {string} key AAAA-MM-DD */
export function fromDateKey(key) {
  const [year, month, day] = key.split('-').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
}

/** Hora local em HH:mm. @param {Date} date */
export function toTimeKey(date) {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Data (AAAA-MM-DD) e hora (HH:mm) locais em ISO. @param {string} dateKey @param {string} timeKey */
export function combineDateAndTime(dateKey, timeKey) {
  const date = fromDateKey(dateKey);
  const [hours, minutes] = timeKey.split(':').map(Number);
  date.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return date.toISOString();
}

/**
 * Intervalo pedido à API para a visão: o mês inclui as semanas completas da grade.
 * @param {'month' | 'week' | 'day'} view @param {Date} anchor
 */
export function getVisibleRange(view, anchor) {
  if (view === 'day') {
    const start = startOfDay(anchor);
    return { start, end: addDays(start, 1) };
  }
  if (view === 'week') {
    const start = startOfWeek(anchor);
    return { start, end: addDays(start, 7) };
  }
  const start = startOfWeek(startOfMonth(anchor));
  return { start, end: addDays(start, 42) };
}

/** @param {'month' | 'week' | 'day'} view @param {Date} anchor @param {number} direction */
export function shiftAnchor(view, anchor, direction) {
  if (view === 'day') return addDays(anchor, direction);
  if (view === 'week') return addDays(anchor, 7 * direction);
  return new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1);
}

/** 42 dias (6 semanas) da grade do mês. @param {Date} anchor */
export function getMonthGrid(anchor) {
  const start = startOfWeek(startOfMonth(anchor));
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

/** @param {'month' | 'week' | 'day'} view @param {Date} anchor */
export function formatRangeTitle(view, anchor) {
  if (view === 'month') {
    const text = new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric' }).format(anchor);
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  if (view === 'day') {
    const text = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(anchor);
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
  const start = startOfWeek(anchor);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const startText = new Intl.DateTimeFormat(LOCALE, sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' }).format(start);
  const endText = new Intl.DateTimeFormat(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' }).format(end);
  return `${startText} a ${endText}`;
}

/** @param {Date} date */
export function formatWeekdayShort(date) {
  return new Intl.DateTimeFormat(LOCALE, { weekday: 'short' }).format(date).replace('.', '');
}

/** Hora no fuso do aparelho (o da grade da agenda), em 12 ou 24 horas conforme a preferência. @param {string} iso */
export function formatTime(iso) {
  return formatPreferredTime(iso, { timeZone: 'device' });
}

/** @param {string} iso */
export function formatLongDate(iso) {
  const text = new Intl.DateTimeFormat(LOCALE, { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso));
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Itens que tocam um dia local.
 * @template {{ startsAt: string, endsAt: string }} TItem
 * @param {TItem[]} items @param {Date} day
 * @returns {TItem[]}
 */
export function itemsOnDay(items, day) {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = dayStart + DAY_MILLISECONDS;
  return items.filter((item) => {
    const start = Date.parse(item.startsAt);
    const end = Math.max(Date.parse(item.endsAt), start + 1);
    return start < dayEnd && end > dayStart;
  });
}

/**
 * Posição dos eventos com hora num dia: topo e altura em minutos desde a meia-noite e coluna dentro do grupo de
 * eventos que se sobrepõem.
 * @template {{ startsAt: string, endsAt: string }} TItem
 * @param {TItem[]} items @param {Date} day
 * @returns {Array<{ item: TItem, startMinute: number, endMinute: number, column: number, columnCount: number }>}
 */
export function layoutDayItems(items, day) {
  const dayStart = startOfDay(day).getTime();
  const positioned = items
    .map((item) => {
      const startMinute = Math.max(0, (Date.parse(item.startsAt) - dayStart) / 60000);
      const endMinute = Math.min(24 * 60, Math.max(startMinute + MINIMUM_EVENT_MINUTES, (Date.parse(item.endsAt) - dayStart) / 60000));
      return { item, startMinute, endMinute, column: 0, columnCount: 1 };
    })
    .sort((left, right) => left.startMinute - right.startMinute || right.endMinute - left.endMinute);
  /** @type {typeof positioned} */
  let cluster = [];
  let clusterEnd = -1;
  const flush = () => {
    const columnCount = Math.max(1, ...cluster.map((entry) => entry.column + 1));
    for (const entry of cluster) entry.columnCount = columnCount;
    cluster = [];
  };
  for (const entry of positioned) {
    if (entry.startMinute >= clusterEnd) flush();
    /** @type {number[]} */
    const columnEnds = [];
    for (const other of cluster) columnEnds[other.column] = Math.max(columnEnds[other.column] ?? 0, other.endMinute);
    let column = columnEnds.findIndex((end) => end === undefined || end <= entry.startMinute);
    if (column === -1) column = columnEnds.length;
    entry.column = column;
    cluster.push(entry);
    clusterEnd = Math.max(clusterEnd, entry.endMinute);
  }
  flush();
  return positioned;
}
